import React, { useContext, useEffect, useMemo, useState } from "react";
import CustomDateRangePicker from "../CustomDateRangePicker/CustomDateRangePicker";
import { TranslationsContext } from "../../utils/TranslationsContext";
import { belgradeParts, fromBelgrade } from "../../utils/belgradeTime";
import "./styles.css";

// Date range + start/end time for an event's duration or registration
// period. Days and times are Belgrade wall-clock (see utils/belgradeTime.js),
// whatever zone the browser is in; from/to come in and go out as ISO
// instants. Picking only days keeps the old meaning of a whole-day range:
// start 00:00, end 23:59.
const DEFAULT_FROM = "00:00";
const DEFAULT_TO = "23:59";

const pad = (n) => String(n).padStart(2, "0");

// 15-minute slots plus 23:59 ("until the end of the day").
const SLOTS = [
  ...Array.from({ length: 96 }, (_, i) => `${pad(Math.floor(i / 4))}:${pad((i % 4) * 15)}`),
  DEFAULT_TO,
];

const timeOf = (iso, fallback) => {
  const p = belgradeParts(iso);
  return p ? `${pad(p.hours)}:${pad(p.minutes)}` : fallback;
};

// The calendar works on local Date objects; give it the Belgrade calendar day.
const calendarDay = (iso) => {
  const p = belgradeParts(iso);
  return p ? new Date(p.year, p.month - 1, p.day) : null;
};

const toIso = (day, time) => {
  if (!day) return "";
  const [h, m] = time.split(":").map(Number);
  return fromBelgrade(day.getFullYear(), day.getMonth() + 1, day.getDate(), h, m);
};

const EventDateTimeRange = ({ label, required, from, to, onChange, error }) => {
  const { t } = useContext(TranslationsContext);
  const [fromTime, setFromTime] = useState(() => timeOf(from, DEFAULT_FROM));
  const [toTime, setToTime] = useState(() => timeOf(to, DEFAULT_TO));

  // Follow values loaded/changed from outside (editing an existing event).
  useEffect(() => {
    if (from) setFromTime(timeOf(from, DEFAULT_FROM));
  }, [from]);
  useEffect(() => {
    if (to) setToTime(timeOf(to, DEFAULT_TO));
  }, [to]);

  const fromDay = calendarDay(from);
  const toDay = calendarDay(to);

  const handleDays = ([start, end]) => {
    onChange(toIso(start, fromTime), toIso(end, toTime));
  };

  const handleTime = (which, value) => {
    if (which === "from") {
      setFromTime(value);
      if (fromDay) onChange(toIso(fromDay, value), to || "");
    } else {
      setToTime(value);
      if (toDay) onChange(from || "", toIso(toDay, value));
    }
  };

  const optionsFor = (value) =>
    SLOTS.includes(value) ? SLOTS : [...SLOTS, value].sort();

  const endBeforeStart =
    from && to && new Date(to).getTime() <= new Date(from).getTime();

  const timeSelect = (which, value, labelKey) => (
    <label className="flex-direction-column gap-4 event-time-field">
      <span className="font-12 inria-sans-700">{t(labelKey)}</span>
      <select
        className="event-time-select inria-sans-400 font-14"
        value={value}
        onChange={(e) => handleTime(which, e.target.value)}
      >
        {optionsFor(value).map((slot) => (
          <option key={slot} value={slot}>
            {slot}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="flex-direction-column gap-8 width-100 event-date-time-range">
      <CustomDateRangePicker
        label={label}
        required={required}
        mode="range"
        dateRange={[fromDay, toDay]}
        setDateRange={handleDays}
        error={error || endBeforeStart}
      />
      <div className="flex-direction-row gap-12 width-100 event-time-row">
        {timeSelect("from", fromTime, "TIME_FROM")}
        {timeSelect("to", toTime, "TIME_TO")}
      </div>
      {endBeforeStart && (
        <p className="font-12 inria-sans-400 event-time-error">
          {t("ERROR_END_BEFORE_START")}
        </p>
      )}
    </div>
  );
};

export default EventDateTimeRange;
